import { Fragment, useEffect, useState } from "react"
import { func, string, shape } from "prop-types"
import { HelperShelfMessage, HelperShelfTrigger } from "components/helper_shelf"
import { css } from "@emotion/core"
import Icon from "components/planning_center_icon"

const RegistrationTypeStep = ({
  event = {},
  setEvent = () => {},
  setNextButtonDisabled = () => {},
  organization: { supportedCurrencies, currentCurrency },
  signupPurpose = "specific"
}) => {
  const [registrationType, setRegistrationType] = useState(event.registrationType)

  const [currency, setCurrency] = useState(currentCurrency)

  useEffect(() => {
    event.registrationType = registrationType
    event.currency = currency
    setEvent(event)
    setNextButtonDisabled(!registrationType)
  }, [event, registrationType, setEvent, setNextButtonDisabled, currency])

  const styles = {
    icon: css`
      left: 1px;
      position: absolute;
      top: 4px;
    `,
    radio: css`
      padding-left: 32px;
      &:before {
        top: 30px !important;
      }
      &:after {
        top: 35px !important;
      }
    `
  }

  return (
    <div>
      <Fragment>
        <h2 className="mb-3">
          What kind of registrations do you want to collect?
          <span className="ml-4p font-size-4">
            <HelperShelfTrigger id="signup_types" />
          </span>
        </h2>
        <div css={{ marginTop: -16, marginBottom: 16 }}>
          <HelperShelfMessage id="signup_types">
            <p>
              <strong>Detailed</strong> is the most common option and is recommended for events like
              VBS and camp. You can collect personal information and answers to custom questions,
              and you can offer add-ons for things like shirts and meals.
            </p>
            <p>
              <strong>Simple</strong> signups are perfect for when you don&#39;t need information
              about each attendee but just want a headcount. Find out how many people are attending
              and collect registrants&#39; names and email addresses.
            </p>
          </HelperShelfMessage>
        </div>
        <div className="mb-3">
          <input
            className="radio"
            type="radio"
            id="detailed"
            name="restriction_type"
            value="detailed"
            onClick={() => {
              setRegistrationType("detailed")
            }}
            defaultChecked={registrationType === "detailed"}
          />

          <label className="radio-label" htmlFor="detailed" css={styles.radio}>
            <div
              css={css`
                ${styles.icon};
                font-size: 22px;
                left: -1px;
                top: -3px;
              `}
              className="c-neutral-300"
            >
              <Icon symbol="registrations#person-details" />
            </div>
            <strong className="c-neutral-600">Detailed</strong>
            <div css={{ marginTop: 4 }}>
              {signupPurpose == "ongoing" ? (
                <div>
                  Gather specific information about every person signing up and collect payments.
                </div>
              ) : (
                <div>
                  Gather specific information about every person attending your event and collect
                  payments.
                </div>
              )}
            </div>
          </label>
        </div>
        <div className="mb-3">
          <input
            className="radio"
            type="radio"
            id="simple"
            name="restriction_type"
            value="simple"
            onClick={() => {
              setRegistrationType("simple")
            }}
            defaultChecked={registrationType === "simple"}
          />
          <label className="radio-label" htmlFor="simple" css={styles.radio}>
            <div
              css={css`
                ${styles.icon};
                font-size: 16px;
                top: 2px;
              `}
              className="c-neutral-300"
            >
              <Icon symbol="general#three-horizontal-list-items" />
            </div>
            <strong className="c-neutral-600">Simple</strong>
            <div css={{ marginTop: 4 }}>
              Registrants simply select the number of people attending.
            </div>
          </label>
        </div>
        <div>
          <input
            className="radio"
            type="radio"
            id="none"
            name="restriction_type"
            value="none"
            onClick={() => {
              setRegistrationType("none")
            }}
            defaultChecked={registrationType === "none"}
          />
          <label className="radio-label" htmlFor="none" css={styles.radio}>
            <div css={styles.icon} className="c-neutral-300 font-size-4">
              <Icon symbol="general#x" />
            </div>
            <strong className="c-neutral-600">None</strong>
            <div css={{ marginTop: 4 }}>
              {signupPurpose == "ongoing" ? (
                <div>
                  Provide information about an opportunity but don&apos;t collect registrations.
                </div>
              ) : (
                <div>Provide information about an event but don&apos;t collect registrations.</div>
              )}
            </div>
          </label>
          {registrationType === "none" && (
            <p className="font-size-4 info-alert alert mt-2 mb-0">
              For events that don&#39;t require people to sign up, consider{" "}
              <a
                data-pendo="newSignup-useCalendarLink"
                href="https://pcocalendar.zendesk.com/hc/en-us/articles/4408334279579"
              >
                using Calendar
              </a>
            </p>
          )}
        </div>
      </Fragment>

      <div className="mt-3">
        <label className="h3" htmlFor="event_currency">
          Currency
        </label>
        {registrationType === "detailed" ? (
          <div>
            Payments will be made in
            <select
              value={currency}
              onChange={e => setCurrency(e.target.value)}
              onBlur={e => setCurrency(e.target.value)}
              name=""
              id="event_currency"
              className="select w-a ml-1 h-4"
            >
              {supportedCurrencies.map(c => {
                return (
                  <option key={c} value={c}>
                    {c}
                  </option>
                )
              })}
            </select>
          </div>
        ) : (
          <div className="c-neutral-500" css={{ lineHeight: "28px", marginBottom: "4px" }}>
            Payments are only available when using a Detailed signup
          </div>
        )}
      </div>
    </div>
  )
}

RegistrationTypeStep.propTypes = {
  setEvent: func,
  setNextButtonDisabled: func,
  signupPurpose: string,
  event: shape({}),
  organization: shape({
    supportedCurrencies: string,
    currentCurrency: string
  })
}

export default RegistrationTypeStep
